import { BusinessHour, validateBusinessHour } from 'apis/types';
import {
  LabeledReadOnlyInput,
  LabeledReadOnlyRadio,
  ReadOnlyInput,
  ReadOnlyRadio,
} from 'components/Fields';

export interface BusinessHourProps {
  startTime?: string;
  endTime?: string;
  businessHourTypeLabel: string;
  allDay: boolean;
}

interface BusinessHoursProps {
  businessHours: BusinessHourProps[];
  isMobile?: boolean;
}

interface BusinessHourForFirstChildProps extends BusinessHourProps {
  isMobile?: boolean;
}

export default function BusinessHours(businessHour: BusinessHour, isMobile: boolean) {
  switch (businessHour.businessHourType) {
    case 'DAILY':
      return (
        <BusinessHourForFirstChild
          startTime={businessHour.mondayStartTime}
          endTime={businessHour.mondayEndTime}
          businessHourTypeLabel="매일"
          allDay={businessHour.mondayAllDay}
          isMobile={isMobile}
        />
      );
    case 'WEEKDAY_WEEKEND':
      return BusinessHourList({
        businessHours: [
          {
            startTime: businessHour.mondayStartTime,
            endTime: businessHour.mondayEndTime,
            businessHourTypeLabel: '주중',
            allDay: businessHour.mondayAllDay,
          },
          {
            startTime: businessHour.sundayStartTime,
            endTime: businessHour.sundayEndTime,
            businessHourTypeLabel: '주말',
            allDay: businessHour.sundayAllDay,
          },
        ],
        isMobile: isMobile,
      });
    default:
      return BusinessHourList({
        businessHours: [
          {
            startTime: businessHour.mondayStartTime,
            endTime: businessHour.mondayEndTime,
            businessHourTypeLabel: '월요일',
            allDay: businessHour.mondayAllDay,
          },
          {
            startTime: businessHour.tuesdayStartTime,
            endTime: businessHour.tuesdayEndTime,
            businessHourTypeLabel: '화요일',
            allDay: businessHour.tuesdayAllDay,
          },
          {
            startTime: businessHour.wednesdayStartTime,
            endTime: businessHour.wednesdayEndTime,
            businessHourTypeLabel: '수요일',
            allDay: businessHour.wednesdayAllDay,
          },
          {
            startTime: businessHour.thursdayStartTime,
            endTime: businessHour.thursdayEndTime,
            businessHourTypeLabel: '목요일',
            allDay: businessHour.thursdayAllDay,
          },
          {
            startTime: businessHour.fridayStartTime,
            endTime: businessHour.fridayEndTime,
            businessHourTypeLabel: '금요일',
            allDay: businessHour.fridayAllDay,
          },
          {
            startTime: businessHour.saturdayStartTime,
            endTime: businessHour.saturdayEndTime,
            businessHourTypeLabel: '토요일',
            allDay: businessHour.saturdayAllDay,
          },
          {
            startTime: businessHour.sundayStartTime,
            endTime: businessHour.sundayEndTime,
            businessHourTypeLabel: '일요일',
            allDay: businessHour.sundayAllDay,
          },
        ],
        isMobile: isMobile,
      });
  }
}

const BusinessHourList = ({ businessHours, isMobile = false }: BusinessHoursProps) => {
  return businessHours
    .filter(({ allDay, startTime, endTime }) => validateBusinessHour(allDay, startTime, endTime))
    .map((item, i) => {
      if (i === 0) {
        return (
          <BusinessHourForFirstChild
            key={i}
            startTime={item.startTime}
            endTime={item.endTime}
            businessHourTypeLabel={item.businessHourTypeLabel}
            allDay={item.allDay}
            isMobile={isMobile}
          />
        );
      }

      return (
        <BusinessHourConponent
          key={i}
          startTime={item.startTime}
          endTime={item.endTime}
          businessHourTypeLabel={item.businessHourTypeLabel}
          allDay={item.allDay}
        />
      );
    });
};

const BusinessHourForFirstChild = ({
  startTime,
  endTime,
  businessHourTypeLabel,
  allDay,
  isMobile,
}: BusinessHourForFirstChildProps) => {
  if (!validateBusinessHour(allDay, startTime, endTime)) return null;

  const startTimeArr = startTime?.split(':');
  const endTimeArr = endTime?.split(':');

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <LabeledReadOnlyInput
        label="운영시간"
        value={businessHourTypeLabel}
        isMobile={isMobile}
        style={{ width: 100 }}
        isColumn={true}
      />
      <LabeledReadOnlyRadio
        label="24시간"
        value={allDay}
        isMobile={isMobile}
        style={{ width: 58 }}
        isColumn={true}
      />

      <LabeledReadOnlyInput
        label="시작시간"
        value={startTimeArr && !allDay ? `${startTimeArr[0]}시` : '-'}
        isMobile={isMobile}
        style={{ width: `calc((100% - 100px - 58px - 8px * 5) / 4)`, minWidth: 60 }}
        isColumn={true}
      />
      <ReadOnlyInput
        text={startTimeArr && !allDay ? `${startTimeArr[1]}분` : '-'}
        style={{
          marginTop: 'auto',
          width: `calc((100% - 100px - 58px - 8px * 5) / 4)`,
          minWidth: 60,
        }}
      />
      <LabeledReadOnlyInput
        label="종료시간"
        value={endTimeArr && !allDay ? `${endTimeArr[0]}시` : '-'}
        isMobile={isMobile}
        style={{ width: `calc((100% - 100px - 58px - 8px * 5) / 4)`, minWidth: 60 }}
        isColumn={true}
      />
      <ReadOnlyInput
        text={endTimeArr && !allDay ? `${endTimeArr[1]}분` : '-'}
        style={{
          marginTop: 'auto',
          width: `calc((100% - 100px - 58px - 8px * 5) / 4)`,
          minWidth: 60,
        }}
      />
    </div>
  );
};

const BusinessHourConponent = ({
  startTime,
  endTime,
  businessHourTypeLabel,
  allDay,
}: BusinessHourProps) => {
  if (!validateBusinessHour(allDay, startTime, endTime)) return null;

  const startTimeArr = startTime?.split(':');
  const endTimeArr = endTime?.split(':');

  return (
    <div style={{ display: 'flex', gap: '8px', marginTop: 4 }}>
      <ReadOnlyInput text={businessHourTypeLabel} style={{ width: 100 }} />
      <ReadOnlyRadio selected={allDay} style={{ width: 58 }} />
      <ReadOnlyInput
        text={startTimeArr && !allDay ? `${startTimeArr[0]}시` : '-'}
        style={{ flex: 1, minWidth: 60 }}
      />
      <ReadOnlyInput
        text={startTimeArr && !allDay ? `${startTimeArr[1]}분` : '-'}
        style={{ flex: 1, minWidth: 60 }}
      />
      <ReadOnlyInput
        text={endTimeArr && !allDay ? `${endTimeArr[0]}시` : '-'}
        style={{ flex: 1, minWidth: 60 }}
      />
      <ReadOnlyInput
        text={endTimeArr && !allDay ? `${endTimeArr[1]}분` : '-'}
        style={{ flex: 1, minWidth: 60 }}
      />
    </div>
  );
};
